/**
 * Mentions Intelligence Data
 *
 * This file contains curated intelligence sources for the Mentions dashboard.
 * Sources represent high-signal intelligence from:
 * - Congressional investigations
 * - Federal funding exposure to PRC-linked entities
 * - University research security failures
 * - Defense contractor and supply chain risks
 * - Strategic influence operations
 * - Data Abyss flagship reports
 */

export type MentionCategory =
  | 'Congressional'
  | 'Funding'
  | 'University'
  | 'Defense'
  | 'Influence'
  | 'Report';

export type RiskLevel = 'Critical' | 'High' | 'Moderate';

export interface MentionEntry {
  id: string;
  title: string;
  date: string; // ISO date string
  source: string;
  category: MentionCategory;
  riskLevel: RiskLevel;
  url: string;
  summary: string;
  keyRiskTakeaway: string;
}

export const mentionsData: MentionEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // 2026 ENTRIES
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: 'universities-watchlist-funding-2026',
    title:
      'Universities Receive Millions in Funding from Foreign Entities on U.S. Government Watch Lists, Records Show',
    date: '2026-07-21',
    source: 'CBS News',
    category: 'University',
    riskLevel: 'Critical',
    url: 'https://www.cbsnews.com/news/universities-foreign-funding-government-watch-lists/',
    summary:
      'Reporting indicates U.S. universities received substantial funding from foreign entities that appear on U.S. government watch lists, raising concerns about disclosure, oversight, and research security controls.',
    keyRiskTakeaway:
      'Foreign funding from watchlisted entities exposes major compliance and national security gaps across university research ecosystems.',
  },
  {
    id: 'elite-universities-cash-squeeze-china-linked-2026',
    title:
      'Elite Universities Face Federal Cash Squeeze Over Millions Tied to China-Linked Entities',
    date: '2026-07-21',
    source: 'Fox News',
    category: 'University',
    riskLevel: 'High',
    url: 'https://www.foxnews.com/politics/elite-universities-face-federal-cash-squeeze-over-millions-tied-china-linked-entities',
    summary:
      'Coverage details potential federal funding pressure on elite universities after scrutiny of financial ties to China-linked entities and related disclosure concerns.',
    keyRiskTakeaway:
      'Universities with opaque foreign-linked funding face rising federal enforcement risk and potential restrictions on research funding.',
  },
  {
    id: 'inside-data-abyss-emergent-defense-2026',
    title: 'Inside Data Abyss',
    date: '2026-05-29',
    source: 'Emergent Defense',
    category: 'Report',
    riskLevel: 'High',
    url: 'https://emergentdefense.com/content/f/inside-data-abyss',
    summary:
      'Feature article profiling Data Abyss and its investigative work across business, digital, and technology security domains.',
    keyRiskTakeaway:
      'Data Abyss reporting is increasingly shaping public and policy visibility into research security and PRC-linked threat exposure.',
  },
  {
    id: 'pentagon-chinese-linked-firm-threat-vetting-2026',
    title:
      'Pentagon Paying Chinese-Linked Firm to Vet Beijing Military Threats, Critics Warn',
    date: '2026-05-05',
    source: 'Washington Times',
    category: 'Defense',
    riskLevel: 'Critical',
    url: 'https://www.washingtontimes.com/news/2026/may/5/pentagon-paying-chinese-linked-moodys-analytics-vet-beijing-military/',
    summary:
      'Report raises concerns that a Chinese-linked firm was engaged in Pentagon-related threat vetting tied to assessments of Beijing military risk.',
    keyRiskTakeaway:
      'Using Chinese-linked contractors in defense threat analysis creates direct counterintelligence and conflict-of-interest vulnerabilities.',
  },
  {
    id: 'security-agencies-hijacked-networks-cyberattacks-2026',
    title:
      'Security Agencies Say Chinese Hackers Using Hijacked Networks for Large-Scale Cyberattacks',
    date: '2026-04-23',
    source: 'Washington Times',
    category: 'Defense',
    riskLevel: 'Critical',
    url: 'https://www.washingtontimes.com/news/2026/apr/23/security-agencies-say-chinese-hackers-using-hijacked-networks-large/',
    summary:
      'Security agencies report Chinese hackers leveraging hijacked networks to conduct broad cyberattack campaigns at scale.',
    keyRiskTakeaway:
      'State-linked cyber operations using compromised network infrastructure increase attack reach while complicating attribution and defense response.',
  },
  {
    id: 'dataabyss-intelligence-compromised-2026',
    title:
      "Intelligence Compromised: China's Embedded Access to IARPA and U.S. Defense Research",
    date: '2026-02-01',
    source: 'Data Abyss',
    category: 'Report',
    riskLevel: 'Critical',
    url: 'https://www.dataabyss.ai/reports/intelligence-compromised',
    summary:
      "Comprehensive investigation by John Sava reveals China's intelligence and public security apparatus, alongside military-affiliated entities, deliberately extracting lessons, methodologies, and technical knowledge from IARPA-funded programs. Report documents attempts to reverse-engineer research outputs, replicate experimental designs, and adapt Intelligence Community technologies for China's mass surveillance apparatus and strategic military capabilities.",
    keyRiskTakeaway:
      'IARPA projects with clear IC value involve Chinese institutions and PLA-linked personnel, enabling systematic technology transfer to hostile foreign power.',
  },
  {
    id: 'crsi-2026-report',
    title: 'Is Research Security Working? A CRSI Assessment',
    date: '2026-02-01',
    source: 'Coalition for Research Security Initiative',
    category: 'Report',
    riskLevel: 'Critical',
    url: 'https://researchsecurity.org/wp-content/uploads/2026/02/CRSI-IsResSecurityWorking-Final.pdf',
    summary:
      'Comprehensive assessment of U.S. research security programs reveals systemic gaps in institutional compliance and enforcement mechanisms across federal funding agencies.',
    keyRiskTakeaway:
      'Research security frameworks remain largely ineffective at preventing unauthorized technology transfer to foreign adversaries.',
  },
  {
    id: 'dailycaller-iarpa-chinese-researchers-2026',
    title:
      "EXCLUSIVE: US Intel Funded Projects Riddled With Chinese Gov't-Linked Researchers",
    date: '2026-02-23',
    source: 'Daily Caller News Foundation',
    category: 'Funding',
    riskLevel: 'Critical',
    url: 'https://dailycaller.com/2026/02/23/exclusive-us-intel-defense-grants-chinese-researchers/',
    summary:
      'Intelligence Advanced Research Projects Activity (IARPA) awarded more than a dozen sensitive defense grants to researchers affiliated with institutions connected to the Chinese government and military since 2017. At least 14 U.S. defense research projects included investigators simultaneously affiliated with Chinese national laboratories, state surveillance entities, military units, and nuclear weapons development institutions.',
    keyRiskTakeaway:
      "IARPA-funded projects with clear Intelligence Community value continue to involve Chinese institutions and personnel directly tied to the People's Liberation Army, enabling deliberate extraction of methodologies and technical knowledge.",
  },
  {
    id: 'bloomberg-billion-research-2026',
    title: 'U.S. Funded $1 Billion in Research with Chinese Labs',
    date: '2026-02-19',
    source: 'Bloomberg',
    category: 'Funding',
    riskLevel: 'Critical',
    url: 'https://www.bloomberg.com/news/articles/2026-02-19/us-funded-1-billion-in-research-with-chinese-labs-report-says',
    summary:
      'Investigation reveals over $1 billion in U.S. federal research funding flowed to collaborative projects with PRC-linked laboratories over the past decade.',
    keyRiskTakeaway:
      'Massive scale of federally-funded research partnerships with PRC entities indicates fundamental oversight failures.',
  },
  {
    id: 'venezuela-china-listening-2026',
    title: 'U.S. Intelligence Tracks China Listening Post in Venezuela',
    date: '2026-01-08',
    source: 'Washington Times',
    category: 'Influence',
    riskLevel: 'High',
    url: 'https://www.washingtontimes.com/news/2026/jan/8/us-moved-venezuela-china-listening/',
    summary:
      'U.S. intelligence agencies identify and track Chinese signals intelligence facility operating in Venezuela, expanding PRC surveillance capabilities in Western Hemisphere.',
    keyRiskTakeaway:
      'China establishing persistent intelligence collection infrastructure in Americas represents strategic encirclement concern.',
  },
  {
    id: 'dataabyss-reports-2026',
    title: 'Data Abyss Intelligence Reports Repository',
    date: '2026-01-01',
    source: 'Data Abyss',
    category: 'Report',
    riskLevel: 'High',
    url: 'https://www.dataabyss.ai/reports',
    summary:
      'Flagship intelligence products from Data Abyss covering research security, foreign influence, and strategic competition domains.',
    keyRiskTakeaway:
      'Anchor intelligence content for tracking PRC-linked research collaboration and institutional security failures.',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 2025 ENTRIES
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: 'doe-containment-breach-2025',
    title: 'DOE Report: Containment Breach — National Lab Security Failures',
    date: '2025-06-15',
    source: 'House Select Committee on the CCP',
    category: 'Congressional',
    riskLevel: 'Critical',
    url: 'https://chinaselectcommittee.house.gov/sites/evo-subsites/selectcommitteeontheccp.house.gov/files/evo-media-document/doe_report_containment_breach_final_v3_compressed-1.pdf',
    summary:
      'Congressional investigation documents systemic security failures at DOE national laboratories enabling unauthorized access to sensitive research by PRC-linked personnel.',
    keyRiskTakeaway:
      'National laboratories failed to implement basic foreign visitor screening, resulting in exposure of defense-relevant research.',
  },
  {
    id: 'fox-henhouse-2025',
    title:
      'Fox in the Henhouse: Chinese Communist Party Penetration of U.S. Research',
    date: '2025-05-20',
    source: 'House Select Committee on the CCP',
    category: 'Congressional',
    riskLevel: 'Critical',
    url: 'https://selectcommitteeontheccp.house.gov/sites/evo-subsites/selectcommitteeontheccp.house.gov/files/evo-media-document/fox-in-the-henhouse-report-compressed_0.pdf',
    summary:
      'Landmark congressional report detailing CCP talent recruitment programs and their systematic exploitation of U.S. academic research institutions.',
    keyRiskTakeaway:
      'Thousand Talents Program and similar initiatives have compromised hundreds of U.S. researchers across federal agencies.',
  },
  {
    id: 'fox-henhouse-appendix-b-2025',
    title: 'Fox in the Henhouse: Appendix B — Detailed Case Studies',
    date: '2025-05-20',
    source: 'House Select Committee on the CCP',
    category: 'Congressional',
    riskLevel: 'Critical',
    url: 'https://selectcommitteeontheccp.house.gov/sites/evo-subsites/selectcommitteeontheccp.house.gov/files/evo-media-document/Appendix%20B.pdf',
    summary:
      'Detailed appendix containing individual case studies of researchers who participated in undisclosed foreign talent programs.',
    keyRiskTakeaway:
      'Pattern analysis reveals common indicators of foreign talent program participation that went undetected.',
  },
  {
    id: 'nuclear-bomber-trailer-park-2025',
    title:
      'U.S. Nuclear Bomber Fleet Shares Fence with Trailer Park Linked to Chinese Intel',
    date: '2025-11-10',
    source: 'Daily Caller',
    category: 'Defense',
    riskLevel: 'Critical',
    url: 'https://dailycaller.com/2025/11/10/exclusive-us-nuclear-bomber-fleet-shares-fence-trailer-park-linked-chinese-intel-miles-guo/',
    summary:
      'Investigation reveals proximity security concerns around U.S. strategic nuclear bomber bases, with properties linked to individuals connected to Chinese intelligence operations.',
    keyRiskTakeaway:
      'Physical security perimeters around strategic assets may be compromised by adjacent property acquisitions.',
  },
  {
    id: 'universities-chinese-funds-2025',
    title: 'Universities Failed to Disclose Millions in Chinese Funding',
    date: '2025-10-24',
    source: 'Washington Times',
    category: 'University',
    riskLevel: 'High',
    url: 'https://www.washingtontimes.com/news/2025/oct/24/universities-failed-disclose-spending-details-millions-chinese-funds/',
    summary:
      'Audit reveals widespread non-compliance with Section 117 foreign gift reporting requirements among U.S. universities receiving PRC funding.',
    keyRiskTakeaway:
      'Institutional failure to disclose foreign funding undermines oversight and enables influence operations.',
  },
  {
    id: 'elite-capture-universities-2025',
    title:
      'Elite Capture: Chinese Funding of American Universities Raises Foreign Influence Concerns',
    date: '2025-10-24',
    source: 'Washington Times',
    category: 'University',
    riskLevel: 'High',
    url: 'https://www.washingtontimes.com/news/2025/oct/24/elite-capture-chinese-funding-american-universities-raises-foreign/',
    summary:
      'Analysis of elite capture dynamics in American higher education, documenting how PRC funding creates institutional dependencies and policy influence.',
    keyRiskTakeaway:
      'Strategic PRC investments in U.S. universities create leverage over research priorities and policy positions.',
  },
  {
    id: 'house-dod-vetting-2025',
    title: 'House Urges DOD to Get Serious About Vetting Defense Contractors',
    date: '2025-06-17',
    source: 'Daily Caller',
    category: 'Defense',
    riskLevel: 'High',
    url: 'https://dailycaller.com/2025/06/17/exclusive-house-urges-dod-to-get-serious-about-vetting-defense-contractors-on-heels-of-dcnf-report/',
    summary:
      'Congressional pressure on Department of Defense to strengthen contractor vetting procedures following revelations of PRC-linked entities in defense supply chain.',
    keyRiskTakeaway:
      'DOD contractor vetting processes insufficient to identify foreign adversary connections in supply chain.',
  },
  {
    id: 'visa-revocation-chinese-students-2025',
    title:
      'U.S. Targets 270,000 Chinese University Students in Massive Visa Revocation',
    date: '2025-05-29',
    source: 'Washington Times',
    category: 'Influence',
    riskLevel: 'High',
    url: 'https://www.washingtontimes.com/news/2025/may/29/us-targets-270000-chinese-university-students-massive-visa-revocation/',
    summary:
      'Large-scale visa security review targeting Chinese students at U.S. universities with ties to military-civil fusion institutions.',
    keyRiskTakeaway:
      'Magnitude of visa actions indicates scale of concern about PLA-linked students in sensitive research programs.',
  },
  {
    id: 'chinese-supercomputer-threat-2025',
    title:
      'Chinese Supercomputer Used by U.S. Researchers Threatens American Security',
    date: '2025-04-30',
    source: 'Washington Times',
    category: 'Funding',
    riskLevel: 'Critical',
    url: 'https://www.washingtontimes.com/news/2025/apr/30/chinese-supercomputer-used-us-researchers-threatens-american-security/',
    summary:
      'U.S. researchers utilizing sanctioned Chinese supercomputing facilities for federally-funded research, creating data security and export control concerns.',
    keyRiskTakeaway:
      'Use of adversary-controlled computing infrastructure exposes research data and methodologies.',
  },
  {
    id: 'pentagon-research-chinese-military-2025',
    title: 'Pentagon-Funded Research Boosting Chinese Military Capabilities',
    date: '2025-04-29',
    source: 'Washington Times',
    category: 'Funding',
    riskLevel: 'Critical',
    url: 'https://www.washingtontimes.com/news/2025/apr/29/pentagon-funded-research-boosting-chinese-military/',
    summary:
      'Investigation reveals DOD-funded research at U.S. universities has been shared with or replicated by PLA-affiliated institutions.',
    keyRiskTakeaway:
      'Pentagon funding inadvertently advancing Chinese military modernization through compromised research pathways.',
  },
  {
    id: 'texas-am-security-failures-2025',
    title:
      'Texas A&M Accused of Repeated Security Failures Protecting Research',
    date: '2025-04-15',
    source: 'Houston Chronicle',
    category: 'University',
    riskLevel: 'Critical',
    url: 'https://www.houstonchronicle.com/news/houston-texas/education/article/a-m-accused-repeated-security-failures-protecting-21965490.php',
    summary:
      'Texas A&M University faces congressional scrutiny over repeated failures in federal research security programs (SECURE), with documented instances of unauthorized foreign access.',
    keyRiskTakeaway:
      'Even flagship research security programs fail when institutional compliance is inadequate.',
  },
  {
    id: 'sl-aerospace-chinese-intel-2025',
    title:
      'U.S. Defense Contractor SL Aerospace Had Fighter Jet, Missile Parts with Chinese Influence',
    date: '2025-02-11',
    source: 'Daily Caller',
    category: 'Defense',
    riskLevel: 'Critical',
    url: 'https://dailycaller.com/2025/02/11/exclusive-us-defense-contractor-sl-aerospace-fighter-jet-missile-parts-chinese-influence-agency/',
    summary:
      'Defense contractor producing critical fighter jet and missile components found to have concerning links to Chinese influence operations.',
    keyRiskTakeaway:
      'PRC influence penetration into defense industrial base poses direct threat to weapons system integrity.',
  },
  {
    id: 'sen-ernst-kill-contracts-2025',
    title:
      'Sen. Joni Ernst Urges DOD to Kill Contracts with Chinese Intel-Tied Defense Contractor',
    date: '2025-03-18',
    source: 'Daily Caller',
    category: 'Defense',
    riskLevel: 'High',
    url: 'https://dailycaller.com/2025/03/18/exclusive-sen-joni-ernst-urges-dod-kill-contracts-with-chinese-intel-tied-defense-contractor/',
    summary:
      'Senate pressure to terminate DOD contracts with defense contractors identified as having ties to Chinese intelligence services.',
    keyRiskTakeaway:
      'Congressional action required to enforce contractor security standards after systemic DOD failures.',
  },
  {
    id: 'sanctioned-supercomputers-nuclear-2025',
    title:
      'Pentagon, Energy Dept Nuclear Research Projects Tapped Sanctioned Chinese Supercomputers',
    date: '2025-03-20',
    source: 'Daily Caller',
    category: 'Funding',
    riskLevel: 'Critical',
    url: 'https://dailycaller.com/2025/03/20/exclusive-pentagon-energy-dept-nuclear-research-projects-tapped-sanctioned-chinese-communist-party-supercomputers/',
    summary:
      'Nuclear research projects funded by Pentagon and DOE utilized sanctioned Chinese Communist Party-linked supercomputing facilities.',
    keyRiskTakeaway:
      'Nuclear weapons-related research data potentially exposed through use of adversary supercomputing infrastructure.',
  },
  {
    id: 'senate-national-lab-directors-2025',
    title:
      'Chairman Lee Demands Answers from National Lab Directors Over Chinese Military Ties',
    date: '2025-03-01',
    source: 'Senate Energy Committee',
    category: 'Congressional',
    riskLevel: 'Critical',
    url: 'https://www.energy.senate.gov/2025/3/chairman-lee-demands-answers-from-national-lab-directors-over-alarming-ties-to-chinese-military-supercomputers',
    summary:
      'Senate Energy Committee launches investigation into national laboratory relationships with Chinese military-affiliated supercomputing centers.',
    keyRiskTakeaway:
      'Congressional oversight reveals widespread undisclosed collaboration between DOE labs and PLA-linked facilities.',
  },
  {
    id: 'newsweek-nasa-spying-china-2025',
    title: 'How U.S. Forces and NASA Could Inadvertently Be Spying for China',
    date: '2025-02-15',
    source: 'Newsweek',
    category: 'Defense',
    riskLevel: 'High',
    url: 'https://www.newsweek.com/exclusive-how-us-forces-nasa-could-inadvertently-spying-china-2016700',
    summary:
      'Investigation into how U.S. military and NASA research activities may inadvertently benefit Chinese intelligence through compromised equipment and data pathways.',
    keyRiskTakeaway:
      'Supply chain and data infrastructure vulnerabilities create unintentional intelligence collection opportunities for PRC.',
  },
  {
    id: 'stanford-china-nuclear-2025',
    title:
      'Stanford Earth Sciences Chair Collaborates with China Nuclear Program',
    date: '2025-01-15',
    source: 'Stanford Review',
    category: 'University',
    riskLevel: 'High',
    url: 'https://stanfordreview.org/investigation-stanford-earth-sciences-chair-collaborates-with-chinas-nuclear-program/',
    summary:
      'Investigation reveals Stanford University department chair maintained undisclosed research collaboration with Chinese nuclear weapons program.',
    keyRiskTakeaway:
      'Elite university leadership maintaining foreign weapons program ties indicates failure of conflict of interest oversight.',
  },
  {
    id: 'china-europe-science-lobby-2026',
    title:
      'China Lobbies Europe to Restore Science Ties After U.S. Greenland Threats',
    date: '2026-02-26',
    source: 'Science Business',
    category: 'Influence',
    riskLevel: 'Moderate',
    url: 'https://sciencebusiness.net/news/international-news/china-lobbies-europe-restore-science-ties-after-us-greenland-threats',
    summary:
      'PRC diplomatic campaign to restore and expand scientific cooperation with European partners following U.S. security measures. However, Chinese universities remain systematically riskier partners than US counterparts. China embeds technology and talent transfer mechanisms directly into institutional cooperation frameworks, and all Chinese universities operate under explicit Party oversight structures.',
    keyRiskTakeaway:
      "China adapting engagement strategy to exploit gaps in allied coordination on research security. The Greenland crisis does not alter the structural characteristics of China's research system.",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 2024 ENTRIES
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: 'us-grants-ccp-talent-2024',
    title:
      "EXCLUSIVE: US Gov't Awarded Sensitive Research Grants To Scientists In Chinese Communist Party Talent Programs",
    date: '2024-12-19',
    source: 'Daily Caller',
    category: 'Funding',
    riskLevel: 'Critical',
    url: 'https://dailycaller.com/2024/12/19/exclusive-us-govt-awarded-sensitive-research-grants-scientists-chinese-communist-party-talent-programs/',
    summary:
      'Investigation reveals U.S. government agencies awarded sensitive research grants to scientists participating in Chinese Communist Party talent recruitment programs, despite federal prohibitions.',
    keyRiskTakeaway:
      'Federal grant oversight failures continue to enable funding of researchers with undisclosed foreign talent program participation.',
  },
  {
    id: 'china-space-supremacy-2024',
    title: "China's Quest for Supremacy Moves into Space",
    date: '2024-12-01',
    source: 'Newsweek',
    category: 'Influence',
    riskLevel: 'High',
    url: 'https://www.newsweek.com/2025/01/17/china-space-infrastructure-us-latin-america-chile-argentina-1999644.html',
    summary:
      "Analysis of China's expanding space infrastructure presence in Latin America, documenting strategic positioning in Chile and Argentina that challenges U.S. influence in the Western Hemisphere.",
    keyRiskTakeaway:
      'PRC space program expansion in Americas creates dual-use intelligence collection and anti-satellite warfare capabilities near U.S. strategic interests.',
  },
  {
    id: 'house-gop-russian-research-2024',
    title:
      'House GOP Probes U.S. Taxpayer Money for Russian Medical Researchers',
    date: '2024-04-30',
    source: 'Washington Times',
    category: 'Congressional',
    riskLevel: 'High',
    url: 'https://www.washingtontimes.com/news/2024/apr/30/house-gop-probes-us-taxpayer-money-for-russian-med/',
    summary:
      'House Republicans investigate U.S. taxpayer funding flowing to Russian medical research institutions despite geopolitical tensions and national security concerns.',
    keyRiskTakeaway:
      'Congressional oversight reveals potential vulnerabilities in federal funding controls for adversary nation research partnerships.',
  },
  {
    id: 'ec-republicans-nih-russia-2024',
    title:
      "E&C Republicans Press NIH to Confirm Agency Isn't Funding Russian Research",
    date: '2024-04-30',
    source: 'House Energy and Commerce Committee',
    category: 'Congressional',
    riskLevel: 'High',
    url: 'https://d1dth6e84htgma.cloudfront.net/04_30_24_Letter_to_NIH_re_Russia_881ac29b04.pdf',
    summary:
      'House Energy and Commerce Committee Republicans formally request NIH Director Monica Bertagnolli to confirm the agency is not funding Russian research activities, citing national security implications.',
    keyRiskTakeaway:
      'Congressional pressure on NIH reflects concerns about federal research funding to adversary nations during heightened geopolitical tensions.',
  },
  {
    id: 'ec-republicans-gao-china-nih-2024',
    title:
      'E&C Republicans Ask Government Watchdog to Study Threat of China Exploiting NIH Research',
    date: '2024-04-02',
    source: 'House Energy and Commerce Committee',
    category: 'Congressional',
    riskLevel: 'Critical',
    url: 'https://d1dth6e84htgma.cloudfront.net/04_02_24_Letter_to_GAO_re_National_Security_Concerns_at_NIH_3ffa5ced03.pdf',
    summary:
      'House Energy and Commerce Committee Republicans request GAO investigation into threats of China exploiting NIH-funded research for military advantage and unethical applications.',
    keyRiskTakeaway:
      'Congressional call for watchdog investigation indicates systemic concerns about PRC exploitation of federally-funded biomedical research.',
  },
  {
    id: 'arctic-dual-use-research-2024',
    title: 'PRC Dual-Use Research in the Arctic',
    date: '2024-10-16',
    source: 'House Select Committee on the CCP',
    category: 'Congressional',
    riskLevel: 'High',
    url: 'https://selectcommitteeontheccp.house.gov/sites/evo-subsites/selectcommitteeontheccp.house.gov/files/evo-media-document/10.16.24_PRC%20dual%20use%20research%20in%20the%20Arctic__.pdf',
    summary:
      'Congressional analysis of PRC dual-use research activities in the Arctic region, documenting military-civilian fusion concerns in polar research.',
    keyRiskTakeaway:
      'Arctic research programs serve as vectors for PRC military intelligence collection and technology acquisition.',
  },
];

/**
 * Returns mentions grouped by year in descending order (most recent first)
 */
export function getMentionsByYear(): Map<number, MentionEntry[]> {
  const byYear = new Map<number, MentionEntry[]>();

  mentionsData.forEach((mention) => {
    const year = new Date(mention.date).getFullYear();
    if (!byYear.has(year)) {
      byYear.set(year, []);
    }
    const yearEntries = byYear.get(year);
    if (yearEntries) {
      yearEntries.push(mention);
    }
  });

  // Sort entries within each year by date (most recent first)
  byYear.forEach((entries) => {
    entries.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  });

  // Return sorted by year descending
  return new Map(Array.from(byYear.entries()).sort(([a], [b]) => b - a));
}

/**
 * Get all unique categories
 */
export function getAllCategories(): MentionCategory[] {
  return [
    'Congressional',
    'Funding',
    'University',
    'Defense',
    'Influence',
    'Report',
  ];
}

/**
 * Get all unique risk levels
 */
export function getAllRiskLevels(): RiskLevel[] {
  return ['Critical', 'High', 'Moderate'];
}

/**
 * Get all years present in the data
 */
export function getAllYears(): number[] {
  const years = new Set<number>();
  mentionsData.forEach((mention) => {
    years.add(new Date(mention.date).getFullYear());
  });
  return Array.from(years).sort((a, b) => b - a);
}
